import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Divider,
  Heading,
  Image,
  ListItem,
  OrderedList,
  Text,
  UnorderedList,
} from '@chakra-ui/react'
import { useMemo } from 'react'
import Markdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'

import { Link } from '../../components/Link/Link'
import { PageLayout } from '../../components/PageLayout/PageLayout'
import { Tooltip } from '../../components/Tooltip/Tooltip'
import { browserTabStaticPageTitles } from '../../store/appStore/browserDocumentTitle/browserTabTitleSpec'
import userGuideText from './userGuideText.md?raw'

// Types for TOC
interface TOCItem {
  id: string
  text: string
  level: number
  children: TOCItem[]
}

// Convert headings to anchors
const createAnchorId = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // remove special characters except spaces and hyphens
    .replace(/\s+/g, '-') // handle spaces with hyphens
    .replace(/^-+|-+$/g, '') // handle leading/trailing hyphens
    .replace(/-+/g, '-') // handle multiple hyphens with single hyphen
}

// Parse markdown content, extract headings for TOC
const parseHeadings = (markdown: string): TOCItem[] => {
  const headingRegex = /^(#{2,6})\s+(.+)$/gm

  return Array.from(markdown.matchAll(headingRegex)).map(match => {
    const level = match[1].length
    const text = match[2].trim()
    const id = createAnchorId(text)

    return {
      id,
      text,
      level,
      children: [],
    }
  })
}

const TableOfContents = ({ headings = [] }: { headings?: TOCItem[] }) => {
  if (headings.length === 0) return null

  return (
    <Box
      mb="2rem"
      borderRadius="md"
      border="1px solid"
      borderColor="presets.neutral.20"
      overflow="hidden"
    >
      <Accordion allowToggle>
        <AccordionItem border="none">
          <AccordionButton
            py="1rem"
            px="1rem"
            bg="presets.neutral.5"
            _hover={{ bg: 'presets.neutral.10' }}
            _expanded={{ bg: 'presets.neutral.10' }}
          >
            <Box flex="1" textAlign="left">
              <Heading as="h2" size="sm" color="presets.neutral.90">
                Table of Contents
              </Heading>
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel
            pb="1rem"
            pt="0.5rem"
            px="1rem"
            bg="presets.neutral.2"
          >
            {headings.map(heading => (
              <Box
                key={heading.id}
                pl={`${(heading.level - 2) * 16}px`}
                mb="0.5rem"
              >
                <Link
                  to="#"
                  color="presets.turquoise.60"
                  fontSize="sm"
                  fontWeight={heading.level === 2 ? '600' : '400'}
                  textDecoration="none"
                  _hover={{
                    textDecoration: 'underline',
                    color: 'presets.turquoise.80',
                  }}
                  onClick={e => {
                    e.preventDefault()
                    const element = document.getElementById(heading.id)
                    if (element) {
                      element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                      })
                      // Update URL hash
                      window.history.replaceState(null, '', `#${heading.id}`)
                    }
                  }}
                >
                  {heading.text}
                </Link>
              </Box>
            ))}
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </Box>
  )
}

// heading component
const createHeadingComponent = (
  as: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6',
  size: string,
  mb: string,
  mt: string,
  color: string,
) => {
  return ({ children, ...props }: any) => {
    const id = createAnchorId(children?.toString() || '')
    return (
      <Box>
        <Tooltip label="Copy to Clipboard" placement="top">
          <Box display="inline-block">
            <Heading
              as={as}
              size={size}
              mb={mb}
              mt={mt}
              color={color}
              id={id}
              position="relative"
              cursor="pointer"
              _hover={{
                '&::before': {
                  content: '"#"',
                  position: 'absolute',
                  left: '-1.5rem',
                  color: 'presets.turquoise.50',
                  fontWeight: 'normal',
                  opacity: 0.7,
                },
              }}
              onClick={() => {
                const url = new URL(window.location.href)
                url.hash = id
                navigator.clipboard?.writeText(url.toString())
                // Also update the URL hash
                window.history.replaceState(null, '', `#${id}`)
              }}
              {...props}
            >
              {children}
            </Heading>
          </Box>
        </Tooltip>
      </Box>
    )
  }
}

const markdownContent = userGuideText

export const UserGuidePage = () => {
  const headings = useMemo(() => parseHeadings(markdownContent), [])

  return (
    <PageLayout
      documentTitle={browserTabStaticPageTitles.userGuide}
      headerSlot={<Heading variant="pageTitle">User Guide</Heading>}
    >
      <Box
        p="1rem"
        sx={{
          html: { scrollBehavior: 'smooth' },
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            scrollMarginTop: '2rem', // Offset for fixed headers
          },
        }}
      >
        <TableOfContents headings={headings} />
        <Markdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          components={{
            // Headers
            h1: createHeadingComponent(
              'h1',
              'lg',
              '1rem',
              '1.5rem',
              'presets.neutral.90',
            ),
            h2: createHeadingComponent(
              'h2',
              'md',
              '0.75rem',
              '1.25rem',
              'presets.neutral.80',
            ),
            h3: createHeadingComponent(
              'h3',
              'sm',
              '0.5rem',
              '1rem',
              'presets.neutral.70',
            ),
            h4: createHeadingComponent(
              'h4',
              'sm',
              '0.5rem',
              '0.75rem',
              'presets.neutral.60',
            ),
            h5: createHeadingComponent(
              'h5',
              'sm',
              '0.25rem',
              '0.50rem',
              'presets.neutral.50',
            ),
            h6: createHeadingComponent(
              'h6',
              'xs',
              '0.25rem',
              '0.25',
              'presets.neutral.50',
            ),
            p: ({ children, ...props }) => (
              <Text mb="0.75rem" lineHeight="1.6" {...props}>
                {children}
              </Text>
            ),
            // Lists
            ul: ({ children, ...props }) => (
              <UnorderedList mb="0.75rem" spacing="0.25rem" ps="5" {...props}>
                {children}
              </UnorderedList>
            ),
            ol: ({ children, ...props }) => (
              <OrderedList mb="0.75rem" spacing="0.25rem" ps="5" {...props}>
                {children}
              </OrderedList>
            ),
            li: ({ children, ...props }) => (
              <ListItem {...props}>{children}</ListItem>
            ),
            a: ({ children, href, ...props }) => (
              <Link
                to={href || '#'}
                color="presets.turquoise.50"
                isExternal={href?.startsWith('http')}
                {...props}
              >
                {children}
              </Link>
            ),
            // Tables
            table: ({ children, ...props }) => (
              <Box overflowX="auto" mb="1rem" {...props}>
                <Box
                  as="table"
                  width="100%"
                  sx={{ borderCollapse: 'collapse' }}
                >
                  {children}
                </Box>
              </Box>
            ),
            thead: ({ children, ...props }) => (
              <Box as="thead" bg="presets.neutral.5" {...props}>
                {children}
              </Box>
            ),
            tbody: ({ children, ...props }) => (
              <Box as="tbody" {...props}>
                {children}
              </Box>
            ),
            tr: ({ children, ...props }) => (
              <Box
                as="tr"
                borderBottom="1px solid"
                borderColor="presets.neutral.20"
                {...props}
              >
                {children}
              </Box>
            ),
            th: ({ children, ...props }) => (
              <Box
                as="th"
                p="0.75rem"
                textAlign="left"
                fontWeight="600"
                {...props}
              >
                {children}
              </Box>
            ),
            td: ({ children, ...props }) => (
              <Box as="td" p="0.75rem" {...props}>
                {children}
              </Box>
            ),
            strong: ({ children, ...props }) => (
              <Text
                as="strong"
                fontWeight="600"
                style={{ display: 'inline' }}
                {...props}
              >
                {children}
              </Text>
            ),
            code: ({ children, ...props }) => (
              <Box
                as="code"
                bg="presets.neutral.10"
                px="0.25rem"
                py="0.125rem"
                borderRadius="sm"
                fontSize="sm"
                {...props}
              >
                {children}
              </Box>
            ),
            pre: ({ children, ...props }) => (
              <Box
                as="pre"
                bg="presets.neutral.10"
                p="1rem"
                borderRadius="md"
                overflowX="auto"
                mb="1rem"
                {...props}
              >
                {children}
              </Box>
            ),
            blockquote: ({ children, ...props }) => (
              <Box
                as="blockquote"
                borderLeft="4px solid"
                borderColor="presets.turquoise.50"
                pl="1rem"
                ml="1rem"
                mb="1rem"
                fontStyle="italic"
                {...props}
              >
                {children}
              </Box>
            ),
            hr: ({ ...props }) => <Divider my="1.5rem" {...props} />,
            img: ({ alt, src, ...props }) => (
              <Box mb="1rem" textAlign="center" {...props}>
                <Text fontSize="sm" color="presets.neutral.60">
                  <Image
                    src={src}
                    alt={alt || 'Image not available'}
                    border="1px solid"
                    borderColor="presets.neutral.20"
                    borderRadius="5px"
                    p="0.5rem"
                  />
                </Text>
              </Box>
            ),
          }}
        >
          {markdownContent}
        </Markdown>
      </Box>
    </PageLayout>
  )
}
